/**
 * Patients list + detail, BUILD.md §9.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, fieldErrorsFrom, qs } from '../lib/api';
import { IntakeCard } from '../components/IntakeCard';
import { IntakeFormCard } from '../components/IntakeFormCard';
import { BodyCompositionCard } from '../components/BodyCompositionCard';
import { SideEffectsCard } from '../components/SideEffects';
import { useAuth } from '../lib/auth';
import {
  ageFromDob,
  formatDate,
  formatDateTime,
  fullName,
  initials,
  STATUS_LABEL,
  statusPillClass,
} from '../lib/format';
import {
  DomainScorePills,
  Button,
  overlay,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  InfoHint,
  SearchableSelect,
  LoadingRows,
  PageHeader,
  Pill,
  ScoreGauge,
  ScorePill,
  Table,
  Td,
  Th,
  inputClass,
} from '../components/ui';
import { PatientOverview } from '../components/PatientOverview';
import { IconPlus, IconSearch } from '../components/icons';
import { HrtProgramBanner } from '../components/HrtHeader';
import { useHrtProgram } from '../lib/hrt';

interface PatientRow {
  id: string;
  externalId: string | null;
  firstName: string;
  lastName: string;
  sexAtBirth: 'Female' | 'Male' | null;
  dob: string | null;
  heightIn: number | null;
  createdAt: string;
  _count: { labReports: number; reports: number; inbodyScans: number };
  latestReport: {
    generatedAt: string;
    scoreRun: { metabolicScore: number | null; inflammationScore: number | null; liverScore: number | null };
  } | null;
}

/* ------------------------------------------------------------------ *
 * Goals
 *
 * The Treatment Plan spec (2026-09-17) stores two facts: "Primary Goal: weight
 * loss" and "Measurable Goal: lose 30 lb". The first is a closed list because
 * the therapy engine and, from phase 2, the Clinical Twin cohort query both
 * match on these exact words; the list is fetched rather than copied here so
 * there is only ever one vocabulary.
 *
 * The target is optional on purpose. "Lose 30 lb" has a number, "improve
 * energy" does not, and a made-up number would be worse than none.
 * ------------------------------------------------------------------ */

interface GoalVocabulary {
  goals: string[];
  units: string[];
}

function useGoalVocabulary() {
  return useQuery({
    queryKey: ['goal-vocabulary'],
    queryFn: () => api.get<GoalVocabulary>('/treatments/goals'),
    staleTime: 60 * 60 * 1000,
  });
}

interface GoalFormValue {
  primaryGoal: string;
  goalTargetValue: string;
  goalTargetUnit: string;
}

function GoalFields({
  value,
  onChange,
  fieldErrors,
}: {
  value: GoalFormValue;
  onChange: (next: GoalFormValue) => void;
  fieldErrors: Record<string, string>;
}) {
  const { data } = useGoalVocabulary();

  return (
    <>
      <Field
        label="Primary goal"
        hint="Type to filter. The list is the therapy library's own vocabulary"
        error={fieldErrors.primaryGoal}
      >
        <SearchableSelect
          value={value.primaryGoal}
          placeholder="Search goals"
          options={(data?.goals ?? []).map((goal) => ({
            value: goal,
            label: goal.charAt(0).toUpperCase() + goal.slice(1),
          }))}
          onChange={(next) => onChange({ ...value, primaryGoal: next })}
        />
      </Field>
      <Field
        label="Target"
        hint="Leave blank when the goal has no number"
        error={fieldErrors.goalTargetValue}
      >
        <input
          type="number"
          step="0.1"
          min="0"
          className={inputClass}
          value={value.goalTargetValue}
          onChange={(e) => onChange({ ...value, goalTargetValue: e.target.value })}
        />
      </Field>
      <Field label="Target unit" error={fieldErrors.goalTargetUnit}>
        <select
          className={inputClass}
          value={value.goalTargetUnit}
          onChange={(e) => onChange({ ...value, goalTargetUnit: e.target.value })}
        >
          <option value="">None</option>
          {(data?.units ?? []).map((unit) => (
            <option key={unit} value={unit}>
              {unit}
            </option>
          ))}
        </select>
      </Field>
    </>
  );
}

/** "Lose 30 lb" reads better than "weight loss, target 30 lb" on a header. */
function goalHeadline(
  primaryGoal: string | null,
  targetValue: number | null,
  targetUnit: string | null,
): string | null {
  if (!primaryGoal) return null;
  const goal = primaryGoal.charAt(0).toUpperCase() + primaryGoal.slice(1);
  if (targetValue === null) return goal;
  return `${goal}, target ${targetValue}${targetUnit ? ` ${targetUnit}` : ''}`;
}

/* ------------------------------------------------------------------ *
 * List
 * ------------------------------------------------------------------ */

export function PatientsPage() {
  // The header search lands here with ?search=, so the list opens filtered.
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') ?? '');
  const [showNew, setShowNew] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['patients', search],
    queryFn: () =>
      api.get<{ data: PatientRow[]; total: number }>(`/patients${qs({ search, pageSize: 100 })}`),
  });

  return (
    <>
      <PageHeader
        title="Patients"
        subtitle="Search, review history, and add new patients."
        actions={
          <Button onClick={() => setShowNew((open) => !open)}>
            <IconPlus size={15} />
            Add patient
          </Button>
        }
      />

      {showNew && <NewPatientForm onDone={() => { setShowNew(false); void refetch(); }} />}

      <Card padded={false}>
        <div className="p-4 border-b border-ink/5">
          <div className="relative max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted">
              <IconSearch size={15} />
            </span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name or patient ID"
              aria-label="Search patients"
              className={`${inputClass} pl-9`}
            />
          </div>
        </div>

        {error ? (
          <div className="p-5"><ErrorState error={error} retry={() => void refetch()} /></div>
        ) : isLoading ? (
          <div className="p-5"><LoadingRows rows={6} /></div>
        ) : !data || data.data.length === 0 ? (
          <EmptyState
            title={search ? 'No patients match that search' : 'No patients yet'}
            body={search ? undefined : 'Add your first patient to begin uploading lab reports.'}
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Patient</Th>
                <Th>ID</Th>
                <Th>Sex / Age</Th>
                <Th align="center">Labs</Th>
                <Th align="center">Reports</Th>
                <Th align="right">Latest scores</Th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((patient) => (
                <tr key={patient.id} className="hover:bg-ink/5/70">
                  <Td className="max-sm:whitespace-nowrap">
                    <Link to={`/patients/${patient.id}`} className="flex items-center gap-2.5 group">
                      <span className="grid shrink-0 place-items-center h-7 w-7 rounded-full bg-ink/5 text-[10px] font-semibold text-ink-muted">
                        {initials(patient)}
                      </span>
                      <span className="font-medium text-ink group-hover:text-[var(--brand-accent)]">
                        {fullName(patient)}
                      </span>
                    </Link>
                  </Td>
                  <Td className="text-ink-muted text-xs font-mono max-sm:whitespace-nowrap">
                    {patient.externalId ?? '-'}
                  </Td>
                  <Td className="text-ink-muted max-sm:whitespace-nowrap">
                    {patient.sexAtBirth ?? '-'}
                    {ageFromDob(patient.dob) !== null && ` · ${ageFromDob(patient.dob)}`}
                  </Td>
                  <Td align="center" className="text-ink-muted">{patient._count.labReports}</Td>
                  <Td align="center" className="text-ink-muted">{patient._count.reports}</Td>
                  <Td align="right">
                    {patient.latestReport ? (
                      <DomainScorePills
                        metabolic={patient.latestReport.scoreRun.metabolicScore}
                        inflammation={patient.latestReport.scoreRun.inflammationScore}
                        liver={patient.latestReport.scoreRun.liverScore}
                      />
                    ) : (
                      <span className="text-xs text-ink-muted">-</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

function NewPatientForm({ onDone }: { onDone: () => void }) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    externalId: '',
    sexAtBirth: '' as '' | 'Female' | 'Male',
    dob: '',
    heightIn: '',
    email: '',
    phone: '',
    primaryGoal: '',
    goalTargetValue: '',
    goalTargetUnit: '',
    smsConsent: false,
    subscribe: false,
  });
  const [error, setError] = useState<string | null>(null);
  /** Per-field messages from the API, keyed by field name. */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: () =>
      api.post('/patients', {
        firstName: form.firstName,
        lastName: form.lastName,
        externalId: form.externalId || null,
        // Sex at birth drives six sex-specific markers, so it is worth capturing
        // up front, without it those markers cannot be scored (§5.4, MD09).
        sexAtBirth: form.sexAtBirth || null,
        dob: form.dob || null,
        heightIn: form.heightIn ? Number(form.heightIn) : null,
        email: form.email || null,
        phone: form.phone || null,
        primaryGoal: form.primaryGoal || null,
        goalTargetValue: form.goalTargetValue ? Number(form.goalTargetValue) : null,
        goalTargetUnit: form.goalTargetUnit || null,
        smsConsent: form.smsConsent,
        subscribe: form.subscribe,
      }),
    onSuccess: onDone,
    onError: (caught) => {
      const fields = fieldErrorsFrom(caught);
      setFieldErrors(fields);
      // Only show the banner when nothing landed on a field, otherwise the
      // same failure is reported twice and the generic wording is the louder
      // of the two.
      setError(
        Object.keys(fields).length > 0
          ? null
          : caught instanceof Error
            ? caught.message
            : 'Could not save',
      );
    },
  });

  return (
    <Card className="mb-6" title="New patient">
      <form
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          setFieldErrors({});
          mutation.mutate();
        }}
      >
        <Field label="First name" required error={fieldErrors.firstName}>
          <input required maxLength={80} className={inputClass} value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
        </Field>
        <Field label="Last name" required error={fieldErrors.lastName}>
          <input required maxLength={80} className={inputClass} value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
        </Field>
        <Field label="Patient ID" hint="Your own record number" error={fieldErrors.externalId}>
          <input maxLength={64} className={inputClass} value={form.externalId}
            onChange={(e) => setForm({ ...form, externalId: e.target.value })} />
        </Field>
        <Field label="Sex at birth" hint="Needed for HDL, ferritin, ESR, uric acid, ALT and GGT" error={fieldErrors.sexAtBirth}>
          <select className={inputClass} value={form.sexAtBirth}
            onChange={(e) => setForm({ ...form, sexAtBirth: e.target.value as 'Female' | 'Male' | '' })}>
            <option value="">Not recorded</option>
            <option value="Female">Female</option>
            <option value="Male">Male</option>
          </select>
        </Field>
        <Field label="Date of birth" hint="Age gates every score band" error={fieldErrors.dob}>
          <input type="date" className={inputClass} value={form.dob}
            onChange={(e) => setForm({ ...form, dob: e.target.value })} />
        </Field>
        <Field label="Height (in)" error={fieldErrors.heightIn}>
          <input type="number" step="0.1" min="1" max="96" className={inputClass} value={form.heightIn}
            onChange={(e) => setForm({ ...form, heightIn: e.target.value })} />
        </Field>
        <Field label="Email" error={fieldErrors.email}>
          <input type="email" className={inputClass} value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label="Mobile" error={fieldErrors.phone}>
          <input type="tel" maxLength={32} className={inputClass} value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>

        <GoalFields
          value={form}
          onChange={(next) => setForm({ ...form, ...next })}
          fieldErrors={fieldErrors}
        />

        {/*
          * Consent and enrolment sit together at the foot of the form because
          * both are decisions the provider makes about this patient, not facts
          * about them. Consent is disabled without a mobile: consenting to
          * texts we have no number for records something that cannot be true.
          */}
        <div className="sm:col-span-2 lg:col-span-4 border-t border-ink/10 pt-4 space-y-3">
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              disabled={!form.phone}
              checked={form.smsConsent}
              onChange={(e) => setForm({ ...form, smsConsent: e.target.checked })}
            />
            <span>
              This patient agreed to receive text messages
              <span className="block text-xs text-ink-muted">
                Required before any text can be sent, including the monthly check-in.
                We record the date and who took the consent.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.subscribe}
              onChange={(e) => setForm({ ...form, subscribe: e.target.checked })}
            />
            <span>
              Enrol in the six month monitoring programme
              <span className="block text-xs text-ink-muted">
                Starts billing only. No lab order is created here.
              </span>
            </span>
          </label>
        </div>

        {error && <p className="sm:col-span-2 lg:col-span-4 text-sm text-red-600">{error}</p>}

        <div className="sm:col-span-2 lg:col-span-4 flex gap-2">
          <Button type="submit" loading={mutation.isPending}>Save patient</Button>
          <Button variant="secondary" onClick={onDone}>Cancel</Button>
        </div>
      </form>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Detail
 * ------------------------------------------------------------------ */

interface PatientDetail extends PatientRow {
  email: string | null;
  phone: string | null;
  smsOptOut: boolean;
  emailOptOut: boolean;
  /** Null when consent was never taken, which is not the same as opted out. */
  smsConsentAt: string | null;
  /** Whether enrolling texts the intake form, and why not when it does not. */
  intakeText?: { willSend: boolean; reason: string | null };
  primaryGoal: string | null;
  goalTargetValue: number | null;
  goalTargetUnit: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  subscription: {
    id: string;
    status: string;
    priceCents: number;
    installmentsPaid: number;
    cycleMonths: number;
  } | null;
  labReports: Array<{
    id: string;
    collectionDate: string;
    status: string;
    createdAt: string;
    _count: { values: number };
    scoreRuns: Array<{
      id: string;
      validationStatus: string;
      confidence: string;
      overallScore: number | null;
      metabolicScore: number | null;
      inflammationScore: number | null;
      liverScore: number | null;
    }>;
  }>;
  inbodyScans: Array<{
    id: string;
    scanDate: string;
    isBaseline: boolean;
    weightLb: number | null;
    smmLb: number | null;
    percentBodyFat: number | null;
    visceralFatLevel: number | null;
  }>;
  reports: Array<{
    id: string;
    generatedAt: string;
    comparisonType: string;
    pdfBytes: number | null;
    scoreRun: {
      metabolicScore: number | null;
      inflammationScore: number | null;
      liverScore: number | null;
      confidence: string;
    };
  }>;
}

export function PatientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['patient', id],
    queryFn: () => api.get<PatientDetail>(`/patients/${id}`),
    enabled: Boolean(id),
  });
  // An HRT / TRT program member gets the hormone program box and the way into its plan.
  const hrtRow = useHrtProgram().data?.patients.find((row) => row.patientId === id) ?? null;

  const latestRun = data?.labReports.find((report) => report.scoreRuns.length > 0)?.scoreRuns[0];

  if (error) return <ErrorState error={error} retry={() => void refetch()} />;
  if (isLoading || !data) return <LoadingRows rows={8} />;

  return (
    <>
      <PageHeader
        title={fullName(data)}
        subtitle={[
          data.externalId && `ID ${data.externalId}`,
          data.sexAtBirth,
          ageFromDob(data.dob) !== null && `${ageFromDob(data.dob)} years`,
          data.heightIn && `${data.heightIn} in`,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Edit details
            </Button>
            <EnrolButton patient={data} onDone={() => void refetch()} />
            <PortalInviteButton patientId={data.id} hasEmail={Boolean(data.email)} />
            <Link to={`/labs?upload=1&patientId=${data.id}`}>
              <Button>Upload lab report</Button>
            </Link>
          </>
        }
      />

      {/*
        * The goal, above everything.
        *
        * Treatment Plan spec, UX requirement: "The patient goal must be
        * visually obvious ... The clinician should understand the patient goal
        * before reviewing peptide options." It sits between the header and the
        * clinical content for that reason, not as decoration.
        */}
      {hrtRow && <HrtProgramBanner row={hrtRow} />}
      {hrtRow && (!data.primaryGoal || data.primaryGoal.trim().toLowerCase() === hrtRow.goal?.trim().toLowerCase()) ? null : goalHeadline(data.primaryGoal, data.goalTargetValue, data.goalTargetUnit) ? (
        <div className="mb-6 rounded-lg border border-[var(--brand-accent)]/25 bg-[var(--brand-accent)]/5 px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-ink-muted">Primary goal</p>
          <p className="text-lg font-semibold text-ink">
            {goalHeadline(data.primaryGoal, data.goalTargetValue, data.goalTargetUnit)}
          </p>
        </div>
      ) : (
        <div className="mb-6 rounded-lg border border-dashed border-ink/15 px-4 py-3">
          <p className="text-sm text-ink-muted">
            No goal recorded yet. A treatment plan cannot report goal attainment without one.
          </p>
        </div>
      )}

      {editing && (
        <EditPatientForm
          patient={data}
          onDone={() => {
            setEditing(false);
            void refetch();
          }}
        />
      )}

      {/*
        * The clinical picture, above the record.
        *
        * 18 September: "optimize the doctor's portal for desktop". The rings,
        * the progress chart and the lab highlights answer "how is this patient
        * doing" at a glance; the tables below answer "what is on file". A
        * doctor opening a patient asks the first question, and it used to take
        * three clicks into a report to answer it.
        *
        * It renders nothing at all until a report has been released, which is
        * correct: before that there are no scores to draw.
        */}
      <PatientOverview patientId={data.id} />

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6">
        <div className="space-y-6">
          <Card title="Lab reports" padded={false}>
            {data.labReports.length === 0 ? (
              <EmptyState title="No lab reports yet" />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Collection date</Th>
                    <Th align="center">Values</Th>
                    <Th align="center">Status</Th>
                    <Th align="right">Scores</Th>
                    <Th align="right"> </Th>
                  </tr>
                </thead>
                <tbody>
                  {data.labReports.map((report) => (
                    <tr key={report.id} className="hover:bg-ink/5/70">
                      <Td className="font-medium text-ink">
                        {formatDate(report.collectionDate)}
                      </Td>
                      <Td align="center" className="text-ink-muted">{report._count.values}</Td>
                      <Td align="center">
                        <Pill className={statusPillClass(report.status)}>
                          {STATUS_LABEL[report.status] ?? report.status}
                        </Pill>
                      </Td>
                      <Td align="right">
                        <DomainScorePills
                          metabolic={report.scoreRuns[0]?.metabolicScore}
                          inflammation={report.scoreRuns[0]?.inflammationScore}
                          liver={report.scoreRuns[0]?.liverScore}
                        />
                      </Td>
                      <Td align="right">
                        <Link
                          to={`/labs/${report.id}`}
                          className="text-xs font-medium text-[var(--brand-accent)] hover:underline"
                        >
                          Open
                        </Link>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          <RegimenCard patientId={data.id} />

          {/* LR-18: side effect reports, with MedWatch. */}
          <SideEffectsCard patientId={data.id} />

          {/* §0.3, the InBody card only appears when the clinic has it enabled. */}
          {user?.tenant.inbodyEnabled && (
            <Card
              title="Body composition scans"
              padded={false}
              action={
                <Link
                  to={`/patients/${data.id}/inbody`}
                  className="text-xs font-medium text-[var(--brand-accent)] hover:underline"
                >
                  Add scan
                </Link>
              }
            >
              {data.inbodyScans.length === 0 ? (
                <EmptyState
                  title="No InBody scans"
                  body="Add a scan to include the body-composition section in this patient's report."
                />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>Scan date</Th>
                      <Th align="right">Weight</Th>
                      <Th align="right">SMM</Th>
                      <Th align="right">Body fat</Th>
                      <Th align="right">Visceral</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.inbodyScans.map((scan) => (
                      <tr key={scan.id} className="hover:bg-ink/5/70">
                        <Td className="font-medium text-ink">
                          {formatDate(scan.scanDate)}
                          {scan.isBaseline && (
                            <Pill className="ml-2 bg-ink/5 text-ink-muted ring-ink/10">
                              baseline
                            </Pill>
                          )}
                        </Td>
                        <Td align="right" className="text-ink-muted">
                          {scan.weightLb?.toFixed(1) ?? '-'} lb
                        </Td>
                        <Td align="right" className="text-ink-muted">
                          {scan.smmLb?.toFixed(1) ?? '-'} lb
                        </Td>
                        <Td align="right" className="text-ink-muted">
                          {scan.percentBodyFat?.toFixed(1) ?? '-'}%
                        </Td>
                        <Td align="right" className="text-ink-muted">
                          {scan.visceralFatLevel ?? '-'}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          )}

          <Card title="Generated reports" padded={false}>
            {data.reports.length === 0 ? (
              <EmptyState title="No reports generated yet" />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Generated</Th>
                    <Th align="center">Type</Th>
                    <Th align="center">Confidence</Th>
                    <Th align="right">Scores</Th>
                    <Th align="right"> </Th>
                  </tr>
                </thead>
                <tbody>
                  {data.reports.map((report) => (
                    <tr key={report.id} className="hover:bg-ink/5/70">
                      <Td className="text-ink-muted">{formatDateTime(report.generatedAt)}</Td>
                      <Td align="center" className="text-ink-muted capitalize text-xs">
                        {report.comparisonType.toLowerCase()}
                      </Td>
                      <Td align="center" className="text-ink-muted text-xs">
                        {report.scoreRun.confidence}
                      </Td>
                      <Td align="right">
                        <DomainScorePills
                          metabolic={report.scoreRun.metabolicScore}
                          inflammation={report.scoreRun.inflammationScore}
                          liver={report.scoreRun.liverScore}
                        />
                      </Td>
                      <Td align="right">
                        <Link
                          to={`/reports/${report.id}`}
                          className="text-xs font-medium text-[var(--brand-accent)] hover:underline"
                        >
                          View
                        </Link>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          {/* The questionnaire drives page 2 of the report, so it sits beside the
              scores where a nurse will notice it is missing. Editing is
              clinician-only: it records medical history and a safety attestation. */}
          <IntakeCard
            patientId={data.id}
            canEdit={user?.role === 'CLINICIAN' || user?.role === 'ADMIN'}
          />
          <IntakeFormCard
            patientId={data.id}
            canEdit={user?.role === 'CLINICIAN' || user?.role === 'ADMIN'}
          />
          <BodyCompositionCard patientId={data.id} />
          <Card title="Latest scores">
            {latestRun ? (
              <div className="grid grid-cols-3 gap-2">
                <ScoreGauge score={latestRun.metabolicScore} label="Metabolic" size={88} />
                <ScoreGauge score={latestRun.inflammationScore} label="Inflammation" size={88} />
                <ScoreGauge score={latestRun.liverScore} label="Liver" size={88} />
              </div>
            ) : (
              <p className="text-sm text-ink-muted text-center py-4">Not scored yet.</p>
            )}
          </Card>

          <Card title="Contact">
            <dl className="text-sm space-y-2.5">
              <div className="flex justify-between gap-3">
                <dt className="text-ink-muted">Email</dt>
                <dd className="text-ink truncate">{data.email ?? '-'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-ink-muted">Mobile</dt>
                <dd className="text-ink">{data.phone ?? '-'}</dd>
              </div>
              {/*
                * Three states, not two. "Never consented" is not "opted out":
                * one is permission we were never given, the other is
                * permission withdrawn, and only the first can be fixed by
                * asking. Showing them as the same thing is what would let a
                * clinic believe a patient is contactable when they are not.
                */}
              <div className="flex justify-between gap-3">
                <dt className="text-ink-muted">SMS</dt>
                <dd
                  className={
                    !data.smsConsentAt
                      ? 'text-amber-deep'
                      : data.smsOptOut
                        ? 'text-red-600'
                        : 'text-emerald-700'
                  }
                >
                  {!data.smsConsentAt
                    ? 'No consent on file'
                    : data.smsOptOut
                      ? 'Opted out'
                      : 'Consented'}
                </dd>
              </div>
              {!data.smsConsentAt && (
                <ConsentButton
                  patientId={data.id}
                  hasPhone={Boolean(data.phone)}
                  onDone={() => void refetch()}
                />
              )}
              <div className="flex justify-between gap-3">
                <dt className="text-ink-muted">Email updates</dt>
                <dd className={data.emailOptOut ? 'text-red-600' : 'text-emerald-700'}>
                  {data.emailOptOut ? 'Opted out' : 'Opted in'}
                </dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>
    </>
  );
}


/* ------------------------------------------------------------------ *
 * Treatment plan
 *
 * Read-only for now. The clinician's selection screen is phase 3; this exists
 * so that what phase 1 stores is visible and checkable rather than only
 * provable by a test, and so a dose history has somewhere to be read.
 * ------------------------------------------------------------------ */

interface RegimenRow {
  id: string;
  regimenCode: string;
  combinationKey: string;
  status: 'DRAFT' | 'ACTIVE' | 'STOPPED';
  selectedAt: string;
  startedAt: string | null;
  stoppedAt: string | null;
  stopReason: string | null;
  goalSnapshot: {
    primaryGoal: string | null;
    targetValue: number | null;
    targetUnit: string | null;
  } | null;
  treatments: Array<{
    id: string;
    dose: string | null;
    frequency: string | null;
    route: string | null;
    treatment: { id: string; therapyId: string; name: string; category: string };
  }>;
  doseChanges: Array<{
    id: string;
    previousDose: string | null;
    newDose: string | null;
    previousFrequency: string | null;
    newFrequency: string | null;
    changedAt: string;
    note: string | null;
  }>;
}

const REGIMEN_STATUS_CLASS: Record<RegimenRow['status'], string> = {
  DRAFT: 'bg-ink/10 text-ink-muted',
  ACTIVE: 'bg-emerald-50 text-emerald-700',
  STOPPED: 'bg-ink/10 text-ink-muted',
};

function RegimenCard({ patientId }: { patientId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['regimens', patientId],
    queryFn: () => api.get<{ regimens: RegimenRow[] }>(`/regimens${qs({ patientId })}`),
  });

  const regimens = data?.regimens ?? [];

  return (
    <Card
      title="Treatment plan"
      action={
        <InfoHint label="About the treatment plan">
          The exact combination a clinician selected, with the goal and clinical picture frozen
          as they were at that moment. A dose change is recorded as history, never overwritten.
        </InfoHint>
      }
    >
      {isLoading ? (
        <LoadingRows rows={2} />
      ) : regimens.length === 0 ? (
        <EmptyState
          title="No treatment plan yet"
          body="Nothing has been prescribed through MCOS for this patient."
        />
      ) : (
        <div className="space-y-5">
          {regimens.map((regimen) => (
            <div key={regimen.id} className="border-b border-ink/5 last:border-0 pb-5 last:pb-0">
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="font-mono text-xs text-ink-muted">{regimen.regimenCode}</span>
                <Pill className={REGIMEN_STATUS_CLASS[regimen.status]}>
                  {regimen.status === 'DRAFT'
                    ? 'Draft'
                    : regimen.status === 'ACTIVE'
                      ? 'Active'
                      : 'Stopped'}
                </Pill>
              </div>

              <ul className="text-sm space-y-1.5">
                {regimen.treatments.map((item) => (
                  <li key={item.id} className="flex justify-between gap-3">
                    <span className="text-ink">{item.treatment.name}</span>
                    <span className="text-ink-muted text-right">
                      {[item.dose, item.frequency ?? item.route].filter(Boolean).join(' · ') ||
                        'Dose not set'}
                    </span>
                  </li>
                ))}
              </ul>

              <dl className="mt-3 text-xs text-ink-muted space-y-1">
                <div className="flex justify-between gap-3">
                  <dt>Goal when selected</dt>
                  <dd className="text-ink">
                    {goalHeadline(
                      regimen.goalSnapshot?.primaryGoal ?? null,
                      regimen.goalSnapshot?.targetValue ?? null,
                      regimen.goalSnapshot?.targetUnit ?? null,
                    ) ?? 'None recorded'}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt>Started</dt>
                  <dd className="text-ink">
                    {regimen.startedAt ? formatDate(regimen.startedAt) : 'Not started'}
                  </dd>
                </div>
                {regimen.stoppedAt && (
                  <div className="flex justify-between gap-3">
                    <dt>Stopped</dt>
                    <dd className="text-ink">
                      {formatDate(regimen.stoppedAt)}
                      {regimen.stopReason ? `, ${regimen.stopReason}` : ''}
                    </dd>
                  </div>
                )}
              </dl>

              {regimen.doseChanges.length > 0 && (
                <div className="mt-3 pt-3 border-t border-ink/5">
                  <p className="text-xs uppercase tracking-wide text-ink-muted mb-1.5">
                    Dose history
                  </p>
                  <ul className="text-xs text-ink-muted space-y-1">
                    {regimen.doseChanges.map((change) => (
                      <li key={change.id}>
                        {formatDateTime(change.changedAt)}:{' '}
                        {change.previousDose ?? 'not set'} to {change.newDose ?? 'not set'}
                        {change.note ? `, ${change.note}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


/**
 * Edit a patient after they were created.
 *
 * There was no way to do this at all: a typo in a date of birth or a wrong
 * height meant deleting the patient and typing them in again, which also threw
 * away their lab reports. The API has accepted a PATCH since the beginning,
 * nothing on screen ever called it.
 *
 * Consent is deliberately absent from this form. Consent is taken, not edited,
 * and withdrawal is a different fact recorded elsewhere; an editable tick box
 * would let someone quietly assert a permission that was never given.
 */
function EditPatientForm({
  patient,
  onDone,
}: {
  patient: PatientDetail;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    firstName: patient.firstName,
    lastName: patient.lastName,
    externalId: patient.externalId ?? '',
    sexAtBirth: (patient.sexAtBirth ?? '') as '' | 'Female' | 'Male',
    dob: patient.dob ? patient.dob.slice(0, 10) : '',
    heightIn: patient.heightIn === null ? '' : String(patient.heightIn),
    email: patient.email ?? '',
    phone: patient.phone ?? '',
    primaryGoal: patient.primaryGoal ?? '',
    goalTargetValue: patient.goalTargetValue === null ? '' : String(patient.goalTargetValue),
    goalTargetUnit: patient.goalTargetUnit ?? '',
    addressLine1: patient.addressLine1 ?? '',
    city: patient.city ?? '',
    state: patient.state ?? '',
    postalCode: patient.postalCode ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: () =>
      api.patch(`/patients/${patient.id}`, {
        firstName: form.firstName,
        lastName: form.lastName,
        externalId: form.externalId || null,
        sexAtBirth: form.sexAtBirth || null,
        dob: form.dob || null,
        heightIn: form.heightIn ? Number(form.heightIn) : null,
        email: form.email || null,
        phone: form.phone || null,
        primaryGoal: form.primaryGoal || null,
        goalTargetValue: form.goalTargetValue ? Number(form.goalTargetValue) : null,
        goalTargetUnit: form.goalTargetUnit || null,
        addressLine1: form.addressLine1 || null,
        city: form.city || null,
        // Uppercased because it decides which clinician may order this
        // patient's labs, and "tx" would match nothing.
        state: form.state ? form.state.toUpperCase() : null,
        postalCode: form.postalCode || null,
      }),
    onSuccess: onDone,
    onError: (caught) => {
      const fields = fieldErrorsFrom(caught);
      setFieldErrors(fields);
      setError(
        Object.keys(fields).length > 0
          ? null
          : caught instanceof Error
            ? caught.message
            : 'Could not save',
      );
    },
  });

  return (
    <Card className="mb-6" title="Edit patient">
      <form
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          setFieldErrors({});
          mutation.mutate();
        }}
      >
        <Field label="First name" required error={fieldErrors.firstName}>
          <input required maxLength={80} className={inputClass} value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
        </Field>
        <Field label="Last name" required error={fieldErrors.lastName}>
          <input required maxLength={80} className={inputClass} value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
        </Field>
        <Field label="Patient ID" hint="Your own record number" error={fieldErrors.externalId}>
          <input maxLength={64} className={inputClass} value={form.externalId}
            onChange={(e) => setForm({ ...form, externalId: e.target.value })} />
        </Field>
        <Field label="Sex at birth" error={fieldErrors.sexAtBirth}>
          <select className={inputClass} value={form.sexAtBirth}
            onChange={(e) => setForm({ ...form, sexAtBirth: e.target.value as 'Female' | 'Male' | '' })}>
            <option value="">Not recorded</option>
            <option value="Female">Female</option>
            <option value="Male">Male</option>
          </select>
        </Field>
        <Field label="Date of birth" error={fieldErrors.dob}>
          <input type="date" className={inputClass} value={form.dob}
            onChange={(e) => setForm({ ...form, dob: e.target.value })} />
        </Field>
        <Field label="Height (in)" error={fieldErrors.heightIn}>
          <input type="number" step="0.1" min="1" max="96" className={inputClass} value={form.heightIn}
            onChange={(e) => setForm({ ...form, heightIn: e.target.value })} />
        </Field>
        <Field label="Email" error={fieldErrors.email}>
          <input type="email" className={inputClass} value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label="Mobile" error={fieldErrors.phone}>
          <input type="tel" maxLength={32} className={inputClass} value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>

        <GoalFields
          value={form}
          onChange={(next) => setForm({ ...form, ...next })}
          fieldErrors={fieldErrors}
        />

        {/* Address is here and not on the create form because it arrives from
            the intake questionnaire; this is where it gets corrected. */}
        <Field label="Address" error={fieldErrors.addressLine1}>
          <input maxLength={160} className={inputClass} value={form.addressLine1}
            onChange={(e) => setForm({ ...form, addressLine1: e.target.value })} />
        </Field>
        <Field label="City" error={fieldErrors.city}>
          <input maxLength={80} className={inputClass} value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })} />
        </Field>
        <Field label="State" hint="Two letters. Decides who may order labs" error={fieldErrors.state}>
          <input maxLength={2} className={inputClass} value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })} />
        </Field>
        <Field label="Postal code" error={fieldErrors.postalCode}>
          <input maxLength={12} className={inputClass} value={form.postalCode}
            onChange={(e) => setForm({ ...form, postalCode: e.target.value })} />
        </Field>

        {error && <p className="sm:col-span-2 lg:col-span-4 text-sm text-red-600">{error}</p>}

        <div className="sm:col-span-2 lg:col-span-4 flex gap-2">
          <Button type="submit" loading={mutation.isPending}>Save changes</Button>
          <Button variant="secondary" onClick={onDone}>Cancel</Button>
        </div>
      </form>
    </Card>
  );
}


/**
 * Put a patient on the monitoring programme after they were created.
 *
 * Enrolment normally happens on the Add patient form, but that is not the only
 * moment it happens in a clinic: someone is added during a consultation and
 * decides a week later. Without this the only way back was to delete the
 * patient and type them in again.
 *
 * Says plainly that it starts money, because it does, and says plainly that it
 * does not order anything, because a clinician might reasonably assume it
 * would.
 */
function EnrolButton({
  patient,
  onDone,
}: {
  patient: PatientDetail;
  onDone: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const enrol = useMutation({
    mutationFn: () => api.post(`/patients/${patient.id}/enrol`, {}),
    onSuccess: () => {
      setError(null);
      setConfirming(false);
      onDone();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'Could not enrol this patient'),
  });

  if (patient.subscription?.status === 'ACTIVE') {
    return (
      <span className="inline-flex items-center text-xs text-ink-muted">
        On the programme · {patient.subscription.installmentsPaid} of{' '}
        {patient.subscription.cycleMonths} months billed
      </span>
    );
  }

  /*
   * Confirmed, not one click.
   *
   * Enrolment starts a recurring charge on the clinic's card and texts the
   * patient their intake form. Neither can be taken back from this screen, and
   * the button sits in a row of four where the other three are harmless.
   *
   * The consequence moved out of a hover hint and into the dialog. A hint is
   * read by people who are already hesitating; this has to reach the ones who
   * are not. That also puts the button back on the same line as its
   * neighbours, which the stacked hint had pushed it off.
   */
  return (
    <>
      <Button
        variant="secondary"
        loading={enrol.isPending}
        onClick={() => {
          setError(null);
          setConfirming(true);
        }}
      >
        Enrol in programme
      </Button>

      <ConfirmDialog
        open={confirming}
        title={`Enrol ${patient.firstName} ${patient.lastName}?`}
        confirmLabel="Enrol and start billing"
        loading={enrol.isPending}
        error={error}
        onCancel={() => setConfirming(false)}
        onConfirm={() => enrol.mutate()}
        body={
          <>
            <p>
              This starts the six month monitoring cycle and charges the first
              instalment to the clinic's card today.
            </p>
            <p>
              {patient.intakeText?.willSend
                ? 'It also texts this patient a link to their intake form.'
                : `${patient.intakeText?.reason ?? 'This patient has no mobile number'}, so no intake form will be texted.`}
            </p>
            <p className="text-ink">No lab order is created here.</p>
          </>
        }
      />
    </>
  );
}

/**
 * Record that a patient agreed to be texted, after they were created.
 *
 * Consent is normally taken on the enrolment form, but a patient who declined
 * at the desk and rings back the next day is ordinary. Without this the record
 * could never be corrected and that patient could never be texted at all.
 *
 * Only ever adds consent. Withdrawal is the opt-out flag and is a different
 * fact: clearing the timestamp would destroy the evidence that consent was
 * taken, which is the whole reason it is stored with a date and a name.
 */
function ConsentButton({
  patientId,
  hasPhone,
  onDone,
}: {
  patientId: string;
  hasPhone: boolean;
  onDone: () => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: () => api.patch(`/patients/${patientId}`, { smsConsent: true }),
    onSuccess: () => {
      setError(null);
      onDone();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'Could not record consent'),
  });

  return (
    <div className="pt-2 space-y-1">
      <Button
        variant="secondary"
        disabled={!hasPhone}
        loading={record.isPending}
        onClick={() => record.mutate()}
      >
        Record consent to text
      </Button>
      <p className="text-[11px] text-ink-muted">
        {hasPhone
          ? 'Only tick this once the patient has actually agreed. The date and your name are stored.'
          : 'Add a mobile number first.'}
      </p>
      {error && <p className="text-[11px] text-red-600">{error}</p>}
    </div>
  );
}

/**
 * Invite a patient to the portal.
 *
 * Three things this deliberately shows rather than hides:
 *
 *   1. Whether the email actually went out. `emailStatus` comes straight from
 *      the server, and a clinician who believes an email was sent when it was
 *      not will leave a patient waiting indefinitely.
 *
 *   2. The link itself, always, not only when email fails. Delivery breaks for
 *      ordinary reasons (no provider, a typo, a spam filter) and a practice
 *      that cannot see the link has no way to help. Showing it to the clinician
 *      already looking at that patient's chart grants nothing they did not have.
 *
 *   3. That re-inviting someone who already has a password will reset their
 *      link, so it is a deliberate second click, not a silent side effect.
 */
function PortalInviteButton({
  patientId,
  hasEmail,
}: {
  patientId: string;
  hasEmail: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<{
    created: boolean;
    setupUrl: string | null;
    emailStatus: string;
    sent: boolean;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  // Where the patient stands, read when the window opens.
  const status = useQuery({
    queryKey: ['portal-invite-status', patientId],
    queryFn: () =>
      api.get<{ email: string | null; hasAccount: boolean; hasPassword: boolean }>(
        `/patients/${patientId}/portal-invite`,
      ),
    enabled: open,
  });

  const invite = useMutation({
    mutationFn: (send: boolean) =>
      api
        .post<{ created: boolean; setupUrl: string | null; emailStatus: string }>(
          `/patients/${patientId}/portal-invite`,
          // Someone who already set a password gets a fresh link only because
          // the clinician chose an action in this window, never silently.
          { send, reissue: Boolean(status.data?.hasPassword) },
        )
        .then((data) => ({ ...data, sent: send })),
    onSuccess: async (data) => {
      setResult(data);
      setCopied(false);
      if (!data.sent && data.setupUrl) {
        try {
          await navigator.clipboard.writeText(data.setupUrl);
          setCopied(true);
        } catch {
          setCopied(false);
        }
      }
      void status.refetch();
    },
  });

  const close = () => {
    setOpen(false);
    setResult(null);
    setCopied(false);
    invite.reset();
  };

  if (!hasEmail) {
    return (
      <Button
        variant="secondary"
        disabled
        title="This patient has no email address on file, so no invitation can be sent."
      >
        Invite to portal
      </Button>
    );
  }

  const standing = status.data
    ? status.data.hasPassword
      ? 'This patient already has a portal password. A new link lets them set a new one.'
      : status.data.hasAccount
        ? 'Invited before, but no password set yet. A new link replaces the old one.'
        : 'Not invited yet.'
    : null;

  const emailMessage = (emailStatus: string) =>
    emailStatus === 'SENT'
      ? 'The invitation was emailed to the patient.'
      : emailStatus === 'SKIPPED_NOT_CONFIGURED'
        ? 'No email service is set up on this server, so nothing was emailed. Copy the link and send it yourself.'
        : emailStatus === 'FAILED'
          ? 'The email could not be delivered. Copy the link and send it yourself.'
          : 'No email was sent.';

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        Invite to portal
      </Button>

      {open && overlay(
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-ink/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Invite to the patient portal"
        >
          <div className="mcos-pop glass-prominent rounded-lg2 p-6 w-full max-w-lg">
            <h2 className="font-display text-base font-bold text-ink">Invite to the patient portal</h2>

            {status.isLoading && <p className="mt-2 text-sm text-ink-muted">Checking the patient&rsquo;s account&hellip;</p>}
            {status.data && (
              <p className="mt-2 text-sm text-ink-muted">
                {status.data.email} &middot; {standing}
              </p>
            )}

            {invite.isError && (
              <p className="mt-3 text-sm text-red-600">
                {invite.error instanceof Error ? invite.error.message : 'Something went wrong.'}
              </p>
            )}

            {result?.setupUrl && (
              <>
                <p className="mt-4 text-sm text-ink">
                  {result.sent ? emailMessage(result.emailStatus) : copied ? 'Link copied. Send it to the patient yourself.' : 'Copy the link below and send it to the patient yourself.'}
                </p>
                <div className="mt-3 rounded-control border border-ink/10 bg-white/60 px-3 py-2.5">
                  <code className="text-[11px] break-all text-ink">{result.setupUrl}</code>
                </div>
                <p className="mt-2 text-[11px] text-ink-muted leading-relaxed">
                  The link works once and expires in 72 hours. Anyone holding it can set this
                  patient&rsquo;s password, so send it to them directly.
                </p>
              </>
            )}

            <div className="mt-5 flex flex-wrap gap-2">
              {result?.setupUrl ? (
                <Button
                  size="sm"
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(result.setupUrl!)
                      .then(() => setCopied(true))
                      .catch(() => setCopied(false));
                  }}
                >
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!status.data}
                  loading={invite.isPending && invite.variables === false}
                  onClick={() => invite.mutate(false)}
                >
                  Copy invite link
                </Button>
              )}
              {!(result?.sent && result.emailStatus === 'SENT') && (
                <Button
                  size="sm"
                  disabled={!status.data}
                  loading={invite.isPending && invite.variables === true}
                  onClick={() => invite.mutate(true)}
                >
                  Send invite by email
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={close}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
